import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import en from "./translations/en";
import es from "./translations/es";
import fr from "./translations/fr";

const LanguageContext = createContext();

const dictionaries = { en, es, fr };
const supportedLanguages = ["en", "es", "fr"];

function getBrowserLanguage() {
    if (typeof navigator === "undefined") return "en";
    const lang = navigator.language?.slice(0, 2)?.toLowerCase();
    return supportedLanguages.includes(lang) ? lang : "en";
}

function getInitialLanguage() {
    if (typeof window === "undefined") return "en";
    const saved = localStorage.getItem("language");
    if (saved && supportedLanguages.includes(saved)) return saved;
    return getBrowserLanguage();
}

function getNestedValue(obj, path) {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

function interpolate(template, vars = {}) {
    if (typeof template !== "string") return template;

    return Object.entries(vars).reduce((result, [key, value]) => {
        const safeValue = String(value);

        // Supports both {key} and {{key}}
        result = result.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "g"), safeValue);
        result = result.replace(new RegExp(`\\{${key}\\}`, "g"), safeValue);

        return result;
    }, template);
}

export function LanguageProvider({ children }) {
    const [language, setLanguage] = useState(getInitialLanguage);

    useEffect(() => {
        localStorage.setItem("language", language);
        document.documentElement.lang = language;
    }, [language]);

    const getRaw = useMemo(() => {
        return (key, fallback = null) => {
            const value = getNestedValue(dictionaries[language], key);
            return value ?? fallback;
        };
    }, [language]);

    const t = useMemo(() => {
        return (key, varsOrOptions = {}, fallbackArg) => {
            const hasOptionsObject =
                varsOrOptions &&
                typeof varsOrOptions === "object" &&
                !Array.isArray(varsOrOptions);

            const defaultValue =
                fallbackArg ??
                (hasOptionsObject &&
                Object.prototype.hasOwnProperty.call(varsOrOptions, "defaultValue")
                    ? varsOrOptions.defaultValue
                    : key);

            const vars = hasOptionsObject
                ? Object.fromEntries(
                    Object.entries(varsOrOptions).filter(([k]) => k !== "defaultValue")
                )
                : {};

            let resolvedKey = key;

            // Basic pluralization support:
            // If count exists and count !== 1, try `${key}_plural`
            if (
                Object.prototype.hasOwnProperty.call(vars, "count") &&
                Number(vars.count) !== 1
            ) {
                const pluralKey = `${key}_plural`;
                const pluralValue = getNestedValue(dictionaries[language], pluralKey);

                if (pluralValue != null) {
                    resolvedKey = pluralKey;
                }
            }

            const value = getNestedValue(dictionaries[language], resolvedKey);
            const finalValue = value ?? defaultValue;

            return interpolate(finalValue, vars);
        };
    }, [language]);

    const value = useMemo(
        () => ({
            language,
            setLanguage,
            t,
            getRaw,
            supportedLanguages,
        }),
        [language, t, getRaw]
    );

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error("useLanguage must be used inside LanguageProvider");
    }
    return context;
}